/**
 * Step 4 — Threat Identification.
 *
 * Story 6.A: spinner / failed-hero / table dispatcher; Add / Edit /
 * Delete via the shared FormModal + useConfirm dialog; STRIDE category
 * filter pill row above the table; hierarchical Current/Child split
 * when `analysis_mode === 'hierarchical'`.
 *
 * On save we auto-derive `compromised_property` from `stride_category`
 * per ISO 21434 (Spoofing→Authenticity, etc.) — the form intentionally
 * hides the property field since it's a function of the STRIDE pick.
 */
import React, { useState } from 'react';
import { LoadingState, FailedState } from '../../../components/ui/LoadingState';

import { useConfirm } from '../../../components/ui/AlertDialog';
import { Icon } from '../../../components/ui/Icon';
import CascadeDeleteDialog from '../components/CascadeDeleteDialog';
import ThreatTable from './step4-threats/ThreatTable';
import ThreatFormModal from './step4-threats/ThreatFormModal';
import HierarchicalThreatReview from './step4-threats/HierarchicalThreatReview';
import type { ChildThreatView } from './step4-threats/ChildThreatsTable';
import {
  derivePropertyFromStride,
  type ThreatRecord,
} from './step4-threats/threatFieldSchema';
import { exportThreatsToCsv, parseThreatFile } from './step4-threats/threatCsv';
import type { Step4BodyProps } from './stepBodyProps';
import { notify } from '../../../services/notificationBus';
import styles from './Step4ThreatIdentification.module.css';

// LoadingState/FailedState are JSX primitives; cast for use from TSX
// (mirrors the ButtonAny convention across the step-components).
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;
const FailedStateAny = FailedState as unknown as React.ComponentType<any>;

const CascadeDeleteDialogAny = CascadeDeleteDialog as unknown as React.ComponentType<any>;

interface DependentRecord {
  id?: string;
  type?: string;
  description?: string;
  [k: string]: unknown;
}

interface CascadeState {
  threat: ThreatRecord;
  dependents: DependentRecord[];
}

type ModalState =
  | null
  | { kind: 'add' }
  | { kind: 'edit'; threat: ThreatRecord };

export default function Step4ThreatIdentification({
  status,
  editable,
  structured,
  crud,
  assetOptions,
  scenarioOptions,
  hierarchical,
  errorMessage,
}: Step4BodyProps) {
  const [modal, setModal] = useState<ModalState>(null);
  const [cascade, setCascade] = useState<CascadeState | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  if (status === 'failed') {
    return (
      <FailedStateAny
        variant="page"
        title="Threat analysis failed"
        description={errorMessage ?? structured.error ?? 'The threat analysis agent encountered an error. Revise and retry.'}
      />
    );
  }

  const threats = (structured.data ?? []) as ThreatRecord[];

  const noDataYet = threats.length === 0 && !structured.data;
  if (
    status === 'empty' ||
    status === 'generating' ||
    status === 'revising' ||
    (structured.loading && noDataYet)
  ) {
    return (
      <LoadingStateAny
        variant="page"
        title="Identifying threats"
        description="The threat analysis agent is deriving STRIDE-mapped threats from assets and damage scenarios. This usually takes 1–2 minutes."
      />
    );
  }

  // Always inject the derived compromised_property on save — never
  // surface the field in the form. Mirrors legacy Step4 derivation.
  const withDerivedProperty = (values: Record<string, unknown>): Record<string, unknown> => ({
    ...values,
    compromised_property: derivePropertyFromStride(values.stride_category as string | undefined),
  });

  const handleSave = async (values: Record<string, unknown>) => {
    const patched = withDerivedProperty(values);
    try {
      if (modal?.kind === 'edit') {
        await crud.update(modal.threat.threat_id, patched);
        notify({ type: 'success', message: `Threat "${patched.threat_id}" updated.` });
      } else {
        await crud.add(patched);
        notify({ type: 'success', message: `Threat "${patched.threat_id}" added.` });
      }
      setModal(null);
      structured.refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save threat';
      throw new Error(typeof msg === 'string' ? msg : 'Failed to save threat');
    }
  };

  const handleExport = () => {
    // Export only current-TARA rows in hierarchical mode — child rows
    // are inherited from upstream sessions and live in their own export.
    exportThreatsToCsv(
      hierarchical.enabled
        ? threats.filter((t: any) => t?.source !== 'child_tara')
        : threats,
    );
  };

  const handleImport = async (file: File) => {
    let records: ThreatRecord[];
    try {
      records = await parseThreatFile(file);
    } catch (e: any) {
      notify({
        type: 'error',
        title: 'Import failed',
        message: e?.message || `Could not parse ${file.name}`,
      });
      return;
    }
    if (records.length === 0) {
      notify({ type: 'warning', message: `${file.name} contained no threat rows.` });
      return;
    }

    const existingIds = new Set(threats.map((t) => t.threat_id));
    let added = 0;
    let skipped = 0;
    let failed = 0;
    for (const record of records) {
      if (!record?.threat_id) {
        failed += 1;
        continue;
      }
      if (existingIds.has(record.threat_id)) {
        skipped += 1;
        continue;
      }
      try {
        // Defence-in-depth: re-derive compromised_property on add
        // even if the CSV / JSON had its own value.
        const patched = {
          ...record,
          compromised_property: derivePropertyFromStride(record.stride_category),
        };
        await crud.add(patched as Record<string, unknown>);
        existingIds.add(record.threat_id);
        added += 1;
      } catch {
        failed += 1;
      }
    }

    if (added > 0) structured.refetch();
    const parts: string[] = [];
    if (added) parts.push(`${added} added`);
    if (skipped) parts.push(`${skipped} skipped (duplicate id)`);
    if (failed) parts.push(`${failed} failed`);
    notify({
      type: failed && !added ? 'error' : 'success',
      title: failed && !added ? 'Import failed' : 'Import complete',
      message: parts.join(' · ') || 'No rows processed.',
    });
  };

  const handleDelete = async (target: ThreatRecord) => {
    const ok = await confirm({
      title: 'Delete threat?',
      description: `Delete threat "${target.threat_id}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      media: <Icon name="delete" />,
    });
    if (!ok) return;
    try {
      await crud.remove(target.threat_id);
      notify({ type: 'success', message: `Threat "${target.threat_id}" deleted.` });
      structured.refetch();
    } catch (e: any) {
      // Backend returns 409 with `code === 'HAS_DEPENDENCIES'` when
      // the threat is referenced by downstream records (attack paths,
      // risks). Swap the plain confirm for the cascade dialog so the
      // user can choose between dropping the threat alone (force) or
      // dropping the whole dependency chain (cascade).
      if (e?.code === 'HAS_DEPENDENCIES' && Array.isArray(e?.dependentRecords)) {
        setCascade({ threat: target, dependents: e.dependentRecords as DependentRecord[] });
        return;
      }
      const msg = e?.message || 'Failed to delete threat';
      notify({ type: 'error', title: 'Delete failed', message: msg });
    }
  };

  const handleCascadeAction = async (mode: 'force' | 'cascade') => {
    if (!cascade) return;
    const target = cascade.threat;
    try {
      const result: any = await crud.remove(target.threat_id, { [mode]: true });
      if (mode === 'cascade') {
        const count = Array.isArray(result?.cascade_results) ? result.cascade_results.length : 0;
        notify({
          type: 'info',
          message: `Deleted "${target.threat_id}" and ${count} dependent record${count !== 1 ? 's' : ''}.`,
        });
      } else {
        notify({
          type: 'warning',
          title: 'Deleted with broken references',
          message: `"${target.threat_id}" deleted; dependent records may now reference a missing threat.`,
        });
      }
      setCascade(null);
      structured.refetch();
    } catch (e: any) {
      notify({
        type: 'error',
        title: 'Delete failed',
        message: e?.message || 'Failed to delete threat',
      });
      setCascade(null);
    }
  };

  const newThreats = hierarchical.enabled
    ? threats.filter((t: any) => t?.source !== 'child_tara')
    : threats;
  const childThreats: ChildThreatView[] = hierarchical.enabled
    ? threats.filter((t: any) => t?.source === 'child_tara')
    : [];

  return (
    <div className={styles.bleed}>
      {hierarchical.enabled ? (
        <HierarchicalThreatReview
          newThreats={newThreats}
          childThreats={childThreats}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(threat) => setModal({ kind: 'edit', threat })}
          onDelete={(threat) => handleDelete(threat)}
          onExport={handleExport}
          onImport={handleImport}
          modifications={hierarchical.modifications}
          onToggleRelevance={hierarchical.setRelevance}
          onJustificationChange={hierarchical.setJustification}
          childInputsDisabled={hierarchical.approving || !editable}
          onSaveChildModifications={hierarchical.onApprove}
        />
      ) : (
        <ThreatTable
          threats={threats}
          editable={editable}
          onAdd={() => setModal({ kind: 'add' })}
          onEdit={(threat) => setModal({ kind: 'edit', threat })}
          onDelete={(threat) => handleDelete(threat)}
          onExport={handleExport}
          onImport={handleImport}
        />
      )}
      {modal && (
        <ThreatFormModal
          mode={modal.kind}
          initialThreat={modal.kind === 'edit' ? modal.threat : null}
          assetOptions={assetOptions}
          scenarioOptions={scenarioOptions}
          onSubmit={handleSave}
          onClose={() => setModal(null)}
        />
      )}
      {confirmDialog}
      <CascadeDeleteDialogAny
        isOpen={!!cascade}
        recordType="Threat"
        recordId={cascade?.threat.threat_id ?? ''}
        dependentRecords={cascade?.dependents ?? []}
        isDeleting={crud.saving}
        onClose={() => setCascade(null)}
        onDeleteOnly={() => handleCascadeAction('force')}
        onCascadeDelete={() => handleCascadeAction('cascade')}
      />
    </div>
  );
}
